/**
 * Client-Side Card Specifications Parser
 * 
 * This script loads card specifications content dynamically by:
 * 1. Reading product description from the page
 * 2. Parsing HTML to extract card data (rarity, HP, attacks, etc.)
 * 3. Generating formatted HTML to display the specifications
 * 4. Updating the UI with loading/error states
 * 
 * The settings come from window.hcCardSpecsSettings (set in Liquid template)
 */

class CardSpecificationsParser {
  constructor() {
    // Get display settings from Liquid template (which fields to show, display style, etc.)
    this.settings = window.hcCardSpecsSettings || {};
    this.init();
  }

  init() {
    // Wait for DOM to be ready before trying to parse
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.parseSpecifications());
    } else {
      this.parseSpecifications();
    }
  }

  parseSpecifications() {
    // STEP 1: Check if new layout exists (hc-specs-values-container)
    const valuesContainer = document.getElementById('hc-specs-values-container');
    
    // Also check for old layout containers for backwards compatibility
    const oldContainer = document.getElementById('hc-card-specifications');
    const oldLoadingEl = document.getElementById('hc-card-specs-loading');
    const oldErrorEl = document.getElementById('hc-card-specs-error');
    
    if (!valuesContainer && !oldContainer) {
      return;
    }

    try {
      // STEP 2: Get the product description HTML from the page
      const productDescription = this.getProductDescription();

      if (!productDescription) {
        throw new Error('No product description found');
      }

      // STEP 3: Parse the HTML to extract card specifications (rarity, HP, attacks, etc.)
      const specs = this.parseHTMLToSpecs(productDescription);

      if (Object.keys(specs).length === 0) {
        throw new Error('No specifications found in description');
      }

      // STEP 4: Generate formatted HTML for displaying the specifications
      const html = this.generateSpecsHTML(specs);
      
      // STEP 5: Update the UI - populate both labels and values containers
      const labelsContainer = document.getElementById('hc-specs-labels-container');
      if (valuesContainer && labelsContainer) {
        // Generate both labels and values HTML
        const { labelsHtml, valuesHtml } = this.generateSpecsTable(specs);
        labelsContainer.innerHTML = labelsHtml;
        valuesContainer.innerHTML = valuesHtml;
      } else if (oldContainer) {
        // Old layout: fall back to old container structure
        const oldContentEl = document.getElementById('hc-card-specs-content');
        if (oldContentEl) {
          oldContentEl.innerHTML = html;
          oldContentEl.style.display = 'block';
        }
        if (oldLoadingEl) oldLoadingEl.style.display = 'none';
      }

    } catch (error) {
      // STEP 6: Handle errors
      console.error('Error parsing card specifications:', error);
      if (oldLoadingEl) oldLoadingEl.style.display = 'none';
      if (oldErrorEl) oldErrorEl.style.display = 'block';
    }
  }

  getProductDescription() {
    // Get the product description HTML from the page
    // Try new layout first, then fall back to old layout
    const container = document.getElementById('hc-card-specifications');
    if (container && container.dataset.productDescription) {
      return container.dataset.productDescription;
    }
    
    // Alternative: Get product description from product info element if available
    const productInfo = document.querySelector('[data-update-url="true"]');
    if (productInfo && productInfo.dataset.description) {
      return productInfo.dataset.description;
    }
    
    return null;
  }

  parseHTMLToSpecs(html) {
    const specs = {};

    // Find all <li> tags in the HTML (each contains a card specification)
    // Example: <li><strong>Rarity:</strong> Rare</li>
    const listItems = html.match(/<li[^>]*>[\s\S]*?<\/li>/g) || [];

    listItems.forEach((item, index) => {
      // Extract field name and value using regex
      // Looks for pattern: <strong>Field Name:</strong> Value
      const match = item.match(/<strong>([^:]+):<\/strong>\s*(.*?)(?=<\/li>)/s);
      if (match) {
        // Convert field name to lowercase with underscores (e.g., "Card Type" -> "card_type")
        const fieldName = match[1].trim().toLowerCase().replace(/\s+/g, '_');
        const value = match[2].trim();
        // Clean up the value (remove any remaining HTML tags and normalize whitespace)
        const cleanValue = value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        if (cleanValue) {
          specs[fieldName] = cleanValue;
        }
      }
    });

    return specs;
  }

  generateSpecsTable(specs) {
    // Generate simple 2-column table: labels on left, values on right
    let labelsHtml = '';
    let valuesHtml = '';
    
    // Helper function to create a row
    const addRow = (label, value, isAttack = false, description = '') => {
      if (isAttack) {
        // Attacks: Put everything in the labels column, put empty div in values column
        const attackValueHtml = description 
          ? `<div class="hc-specs-value hc-specs-value--attack">${this.escapeHtml(value)}<br><span class="hc-specs-description">${this.escapeHtml(description)}</span></div>`
          : `<div class="hc-specs-value hc-specs-value--attack">${this.escapeHtml(value)}</div>`;
        
        labelsHtml += `<div class="hc-specs-item hc-specs-item--attack"><div class="hc-specs-label">${label}</div>${attackValueHtml}</div>`;
        valuesHtml += `<div class="hc-specs-item"></div>`; // Empty spacer
      } else {
        // Regular fields: label on left, value on right
        labelsHtml += `<div class="hc-specs-item"><div class="hc-specs-label">${label}</div></div>`;
        valuesHtml += `<div class="hc-specs-item"><div class="hc-specs-value">${this.escapeHtml(value)}</div></div>`;
      }
    };
    
    // Build the table from specs - group fields first, then attacks at the bottom
    if (specs.card_number || specs.number || specs.rarity) {
      const numberValue = specs.card_number || specs.number || '—';
      const rarityValue = specs.rarity || '—';
      addRow('CARD NUMBER / RARITY', `${numberValue} / ${rarityValue}`);
    }
    
    if (specs.card_type || specs.hp || specs.stage) {
      const typeValue = specs.card_type || '—';
      const hpValue = specs.hp || '—';
      const stageValue = specs.stage || '—';
      addRow('CARD TYPE / HP / STAGE', `${typeValue} / ${hpValue} / ${stageValue}`);
    }
    
    // Remaining fields (before attacks)
    if (specs.weakness) {
      addRow('WEAKNESS', specs.weakness);
    }
    if (specs.resistance) {
      addRow('RESISTANCE', specs.resistance);
    }
    if (specs.retreat_cost) {
      addRow('RETREAT COST', specs.retreat_cost);
    }
    
    // Handle all attacks dynamically - put them at the bottom
    for (let i = 1; i <= 5; i++) {
      const attackKey = `attack_${i}`;
      if (specs[attackKey]) {
        const valueParts = this.parseAttackValue(specs[attackKey]);
        addRow(`ATTACK ${i}`, valueParts.main, true, valueParts.description);
      }
    }
    
    return { labelsHtml, valuesHtml };
  }

  generateSpecsHTML(specs) {
    // Legacy method - keep for backwards compatibility
    return '';
  }

  parseAttackValue(value) {
    // Parse attack value to separate the attack name from description
    // Example: "Thunderbolt 30" might have description "- Discard all Energy attached to this Pokémon"
    
    if (!value) return { main: '', description: '' };
    
    // Split by common patterns (newline, dash, etc.)
    const parts = value.split(/[\n\r-]/);
    
    if (parts.length > 1) {
      return {
        main: parts[0].trim(),
        description: parts.slice(1).join(' ').trim()
      };
    }
    
    return { main: value.trim(), description: '' };
  }

  escapeHtml(text) {
    // Safely escape HTML to prevent XSS attacks
    // Creates a temporary div, sets text content, then gets innerHTML (which escapes special chars)
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Prevent redeclaration if script is loaded multiple times
if (typeof window.CardSpecificationsParser === 'undefined') {
  window.CardSpecificationsParser = CardSpecificationsParser;
}

// Initialize the parser with a small delay to ensure DOM is ready
// This runs automatically when the script loads
if (typeof window.CardSpecificationsParser !== 'undefined') {
  setTimeout(() => {
    new window.CardSpecificationsParser();
  }, 100);
}
